/**
 * Expert Consultations Hook
 *
 * FERP Sprint 5 - Professional Features
 *
 * React hook for loading expert consultation bookings from backend.
 * Used by CalendarBooking component to display existing appointments.
 *
 * Architecture Compliance:
 * - ✅ Repository pattern (GraphQL query via Apollo)
 * - ✅ Multi-tenant security (tenant from context)
 * - ✅ Domain-agnostic (all data from backend)
 * - ✅ Life-critical safety (complete error handling)
 */

import { useQuery } from '@apollo/client/react';
import { gql } from '@apollo/client';
import { useCurrentTenant } from './useCurrentTenant';

/**
 * Expert consultation booking record
 */
export interface ExpertConsultation {
  id: string;
  tenant: string;
  expertId: string;
  userId: string;
  scheduledAt: string; // ISO 8601 datetime
  duration: number; // Duration in minutes
  status: 'Scheduled' | 'Completed' | 'Cancelled';
  sessionType: 'Video' | 'Phone' | 'InPerson';
  notes: string;
  createdAt: string;
  createdBy: string;

  // ✅ Populated fields from backend (not hardcoded)
  expertName?: string;
  expertSpecialty?: string;
}

/**
 * GraphQL query for expert consultations
 */
const EXPERT_CONSULTATIONS_QUERY = gql`
  query ExpertConsultations($input: ExpertConsultationsInput!) {
    expertConsultations(input: $input) {
      consultations {
        id
        tenant
        expertId
        userId
        scheduledAt
        duration
        status
        sessionType
        notes
        createdAt
        createdBy
        expertName
        expertSpecialty
      }
      totalCount
    }
  }
`;

/**
 * Input options for useExpertConsultations hook
 */
export interface UseExpertConsultationsOptions {
  /**
   * Filter by expert ID (optional)
   */
  expertId?: string;

  /**
   * Filter by user ID (optional)
   */
  userId?: string;

  /**
   * Filter by status (optional)
   */
  status?: 'Scheduled' | 'Completed' | 'Cancelled';

  /**
   * Start date filter (ISO 8601)
   */
  startDate?: string;

  /**
   * End date filter (ISO 8601)
   */
  endDate?: string;
}

/**
 * Return type for useExpertConsultations hook
 */
export interface UseExpertConsultationsResult {
  /**
   * Array of expert consultations
   */
  consultations: ExpertConsultation[];

  /**
   * Total count (for pagination)
   */
  totalCount: number;

  /**
   * Loading state
   */
  loading: boolean;

  /**
   * Error state
   */
  error: Error | undefined;

  /**
   * Refetch function
   */
  refetch: () => void;
}

/**
 * Hook for loading expert consultation bookings
 *
 * @param options - Filter options
 * @returns Expert consultations query result
 *
 * @example
 * ```tsx
 * function MyCalendar() {
 *   const { consultations, loading } = useExpertConsultations({
 *     startDate: '2025-10-01T00:00:00Z',
 *     endDate: '2025-10-31T23:59:59Z',
 *     status: 'Scheduled',
 *   });
 *
 *   if (loading) return <div>Loading...</div>;
 *
 *   return (
 *     <FullCalendar
 *       events={consultations.map(c => ({
 *         id: c.id,
 *         title: c.expertName,
 *         start: c.scheduledAt,
 *         end: new Date(new Date(c.scheduledAt).getTime() + c.duration * 60000).toISOString(),
 *       }))}
 *     />
 *   );
 * }
 * ```
 */
export function useExpertConsultations(
  options: UseExpertConsultationsOptions = {}
): UseExpertConsultationsResult {
  // ✅ FERP-Compliant: Get tenant from context (not hardcoded)
  const { tenantId } = useCurrentTenant();

  const { data, loading, error, refetch } = useQuery<Record<string, any>>(EXPERT_CONSULTATIONS_QUERY, {
    variables: {
      input: {
        tenant: tenantId,
        expertId: options.expertId || null,
        userId: options.userId || null,
        status: options.status || null,
        startDate: options.startDate || null,
        endDate: options.endDate || null,
      },
    },
    fetchPolicy: 'cache-and-network',
    skip: !tenantId, // Skip query if no tenant context
  });

  return {
    consultations: data?.expertConsultations?.consultations || [],
    totalCount: data?.expertConsultations?.totalCount || 0,
    loading,
    error,
    refetch,
  };
}
